import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { FUNCTIONS_URL } from '../services/supabaseConfig';
import { LEAGUE_TYPE, QL_ARRAY, TRAD_ARRAY } from '../constants/leagueTypes';
import './HatcheryPage.css';

/* ===== TYPES ===== */

interface AIPlayer {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  overall: number;
  is_hitter: boolean;
  team_id: string;
  team_name?: string;
  league_name?: string;
  league_id?: string;
  avatar_url?: string;
  power?: number;
  contact?: number;
  speed?: number;
  discipline?: number;
  velocity?: number;
  control?: number;
  movement?: number;
  deception?: number;
  endurance?: number;
  price: number;
}

interface Egg {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  overall: number;
  is_hitter: boolean;
  avatar_url?: string;
  parent_a_id?: string;
  parent_b_id?: string;
  price: number;
  season_year: number;
}

interface League {
  id: string;
  name: string;
  tier: number;
  season_year: number;
}

interface Team {
  id: string;
  name: string;
  league_id: string;
}

type HatchView = 'field' | 'nest';
type SortKey = 'overall' | 'price' | 'position' | 'name';

const POSITIONS = ['All', 'P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF'];

function getPrice(ovr: number): number {
  // OVR-based: min 1500 BALLS, no maximum, 1000 BALLS to platform
  return Math.max(ovr * 100, 1500);
}

function getEggPrice(ovr: number): number {
  // Eggs are 40% cheaper than hatched players
  return Math.round(getPrice(ovr) * 0.6);
}

function getOvrColor(ovr: number): string {
  if (ovr >= 75) return '#f59e0b';
  if (ovr >= 70) return '#e8b82a';
  if (ovr >= 65) return '#22c55e';
  if (ovr >= 60) return '#3b82f6';
  if (ovr >= 55) return '#8b5cf6';
  return '#9ca3af';
}

function getOvrGrade(ovr: number): string {
  if (ovr >= 75) return 'S';
  if (ovr >= 70) return 'A';
  if (ovr >= 65) return 'B+';
  if (ovr >= 60) return 'B';
  if (ovr >= 55) return 'C';
  return 'D';
}

/* ===== COMPONENT ===== */

interface Props {
  onNavigate?: (page: string) => void;
  userData?: any;
}

const HatcheryPage: React.FC<Props> = ({ onNavigate, userData }) => {
  const [view, setView] = useState<HatchView>('field');
  const [leagues, setLeagues] = useState<League[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<AIPlayer[]>([]);
  const [eggs, setEggs] = useState<Egg[]>([]);
  const [loading, setLoading] = useState(true);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [ballsBalance, setBallsBalance] = useState(0);

  // Filters
  const [selectedLeague, setSelectedLeague] = useState<string>('all');
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [posFilter, setPosFilter] = useState('All');
  const [minOvr, setMinOvr] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('overall');
  const [sortDesc, setSortDesc] = useState(true);

  // Buy flow
  const [buying, setBuying] = useState<string | null>(null);
  const [buyResult, setBuyResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showConfirm, setShowConfirm] = useState<AIPlayer | Egg | null>(null);

  useEffect(() => {
    loadLeagues();
    loadBalance();
  }, []);

  async function loadBalance() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const { data } = await supabase.from('users').select('balls_balance').eq('id', session.user.id).single();
    if (data) setBallsBalance(data.balls_balance || 0);
  }

  async function loadLeagues() {
    setLoading(true);
    const { data } = await supabase.from('leagues')
      .select('id, name, tier, season_year')
      .in('league_type', TRAD_ARRAY as readonly string[])
      .in('status', ['active', 'playoffs', 'offseason', 'off_season'])
      .order('tier', { ascending: true });
    setLeagues(data || []);

    if (data?.length) {
      const { data: teamData } = await supabase.from('teams')
        .select('id, name, league_id')
        .in('league_id', data.map(l => l.id))
        .order('name');
      setTeams(teamData || []);
    }
    setLoading(false);
  }

  // Load hatched players (on teams)
  const loadPlayers = useCallback(async () => {
    setPlayersLoading(true);
    let query = supabase.from('players')
      .select('id, first_name, last_name, position, overall, is_hitter, team_id, avatar_url, power, contact, speed, discipline, velocity, control, movement, deception, endurance, league_id')
      .is('user_id', null)
      .is('retired', false)
      .order('overall', { ascending: false })
      .limit(200);

    if (selectedLeague !== 'all') {
      const leagueTeamIds = teams.filter(t => t.league_id === selectedLeague).map(t => t.id);
      if (leagueTeamIds.length > 0) {
        query = query.in('team_id', leagueTeamIds);
      }
    } else {
      const allTeamIds = teams.map(t => t.id);
      if (allTeamIds.length > 0) {
        query = query.in('team_id', allTeamIds);
      }
    }

    if (selectedTeam !== 'all') {
      query = query.eq('team_id', selectedTeam);
    }

    const { data } = await query;

    const teamMap = new Map(teams.map(t => [t.id, t]));
    const leagueMap = new Map(leagues.map(l => [l.id, l]));
    const enriched: AIPlayer[] = (data || []).map(p => {
      const team = teamMap.get(p.team_id);
      const league = team ? leagueMap.get(team.league_id) : null;
      return {
        ...p,
        team_name: team?.name || 'Unknown',
        league_name: league?.name || '',
        league_id: team?.league_id,
        price: getPrice(p.overall || 50),
      };
    });
    setPlayers(enriched);
    setPlayersLoading(false);
  }, [selectedLeague, selectedTeam, teams, leagues]);

  // Load eggs (draft pool / unhatched)
  const loadEggs = useCallback(async () => {
    setPlayersLoading(true);
    // For now, eggs come from draft_picks or a future eggs table
    // This will be populated by the NFT pre-forge system
    // Placeholder: query players with no team_id and generation >= 2
    const { data } = await supabase.from('players')
      .select('id, first_name, last_name, position, overall, is_hitter, avatar_url, generation, career_season')
      .is('team_id', null)
      .is('retired', false)
      .gte('generation', 2)
      .order('overall', { ascending: false })
      .limit(100);

    const enriched: Egg[] = (data || []).map(p => ({
      id: p.id,
      first_name: p.first_name,
      last_name: p.last_name,
      position: p.position,
      overall: p.overall,
      is_hitter: p.is_hitter,
      avatar_url: p.avatar_url,
      season_year: 1901, // will hatch next season
      price: getEggPrice(p.overall || 50),
    }));
    setEggs(enriched);
    setPlayersLoading(false);
  }, []);

  useEffect(() => {
    if (teams.length > 0) {
      if (view === 'field') loadPlayers();
      else loadEggs();
    }
  }, [view, loadPlayers, loadEggs, teams]);

  // Filter + sort (for both views)
  const filterAndSort = <T extends { overall: number; position: string; first_name: string; last_name: string; price: number }>(items: T[]): T[] => {
    return items
      .filter(p => posFilter === 'All' || p.position === posFilter)
      .filter(p => p.overall >= minOvr)
      .filter(p => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return `${p.first_name} ${p.last_name}`.toLowerCase().includes(q)
          || p.position.toLowerCase().includes(q);
      })
      .sort((a, b) => {
        let cmp = 0;
        switch (sortKey) {
          case 'overall': cmp = a.overall - b.overall; break;
          case 'price': cmp = a.price - b.price; break;
          case 'position': cmp = a.position.localeCompare(b.position); break;
          case 'name': cmp = `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`); break;
        }
        return sortDesc ? -cmp : cmp;
      });
  };

  const filteredPlayers = filterAndSort(players);
  const filteredEggs = filterAndSort(eggs);
  const filteredTeams = selectedLeague === 'all' ? teams : teams.filter(t => t.league_id === selectedLeague);

  // Buy handler (works for both hatched and eggs)
  async function handleBuy(item: AIPlayer | Egg) {
    setBuying(item.id);
    setBuyResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setBuyResult({ success: false, message: 'Please sign in first' }); return; }

      const res = await fetch(`${FUNCTIONS_URL}/buy-ai-player`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'buy', playerId: item.id }),
      });
      const data = await res.json();
      if (res.ok && !data.error) {
        const displayName = `${item.first_name} ${item.last_name}`;
        setBuyResult({ success: true, message: `Acquired ${displayName} for ${item.price.toLocaleString()} $BALLS!` });
        setBallsBalance(prev => prev - item.price);
        // Remove from appropriate list
        if ('team_id' in item) {
          setPlayers(prev => prev.filter(p => p.id !== item.id));
        } else {
          setEggs(prev => prev.filter(e => e.id !== item.id));
        }
      } else {
        setBuyResult({ success: false, message: data.error || 'Purchase failed' });
      }
    } catch (e: any) {
      setBuyResult({ success: false, message: e.message });
    } finally {
      setBuying(null);
      setShowConfirm(null);
    }
  }

  function handleSort(key: SortKey) {
    if (sortKey === key) setSortDesc(!sortDesc);
    else { setSortKey(key); setSortDesc(true); }
  }

  if (loading) {
    return <div className="hatch-page"><div className="hatch-loading">Loading hatchery...</div></div>;
  }

  return (
    <div className="hatch-page">
      {/* Header */}
      <div className="hatch-header">
        <div className="hatch-title-row">
          <h1 className="hatch-title">🥚 The Hatchery</h1>
          <div className="hatch-balance">
            <span className="hatch-balance-label">Balance:</span>
            <span className="hatch-balance-amount">{ballsBalance.toLocaleString()} $BALLS</span>
          </div>
        </div>
        <p className="hatch-subtitle">Acquire players from traditional leagues. Hatched players are on the field now — eggs hatch next season.</p>
      </div>

      {/* View Toggle */}
      <div className="hatch-view-toggle">
        <button
          className={`hatch-toggle-btn ${view === 'field' ? 'active' : ''}`}
          onClick={() => setView('field')}
        >
          ⚾ On the Field ({players.length})
        </button>
        <button
          className={`hatch-toggle-btn ${view === 'nest' ? 'active' : ''}`}
          onClick={() => setView('nest')}
        >
          🥚 In the Nest ({eggs.length})
        </button>
      </div>

      {/* Filters */}
      <div className="hatch-filters">
        <div className="hatch-filter-row">
          {view === 'field' && (
            <>
              <select className="hatch-select" value={selectedLeague} onChange={e => { setSelectedLeague(e.target.value); setSelectedTeam('all'); }}>
                <option value="all">All Leagues</option>
                {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>

              <select className="hatch-select" value={selectedTeam} onChange={e => setSelectedTeam(e.target.value)}>
                <option value="all">All Teams</option>
                {filteredTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </>
          )}

          <input
            className="hatch-search"
            placeholder="Search name, position..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="hatch-filter-row">
          <div className="hatch-pos-pills">
            {POSITIONS.map(pos => (
              <button
                key={pos}
                className={`hatch-pill${posFilter === pos ? ' hatch-pill-active' : ''}`}
                onClick={() => setPosFilter(pos)}
              >
                {pos}
              </button>
            ))}
          </div>

          <div className="hatch-ovr-filter">
            <span className="hatch-ovr-label">Min OVR: {minOvr}</span>
            <input type="range" min={0} max={80} step={5} value={minOvr} onChange={e => setMinOvr(Number(e.target.value))} className="hatch-slider" />
          </div>
        </div>

        <div className="hatch-sort-row">
          <span className="hatch-sort-label">Sort:</span>
          {(['overall', 'price', 'position', 'name'] as SortKey[]).map(k => (
            <button key={k} className={`hatch-sort-btn${sortKey === k ? ' active' : ''}`} onClick={() => handleSort(k)}>
              {k.charAt(0).toUpperCase() + k.slice(1)} {sortKey === k ? (sortDesc ? '▼' : '▲') : ''}
            </button>
          ))}
          <span className="hatch-count">
            {view === 'field' ? filteredPlayers.length : filteredEggs.length} {view === 'field' ? 'players' : 'eggs'}
          </span>
        </div>
      </div>

      {/* Price legend */}
      <div className="hatch-price-legend">
        {view === 'field' ? (
          <>
            <span className="hatch-legend-item"><span className="hatch-dot" style={{background:'#f59e0b'}}></span> 70+: 7,500</span>
            <span className="hatch-legend-item"><span className="hatch-dot" style={{background:'#22c55e'}}></span> 65-69: 5,000</span>
            <span className="hatch-legend-item"><span className="hatch-dot" style={{background:'#3b82f6'}}></span> 60-64: 3,750</span>
            <span className="hatch-legend-item"><span className="hatch-dot" style={{background:'#8b5cf6'}}></span> 55-59: 2,500</span>
            <span className="hatch-legend-item"><span className="hatch-dot" style={{background:'#9ca3af'}}></span> &lt;55: 1,500</span>
          </>
        ) : (
          <span className="hatch-legend-note">🥚 Egg prices are 40% off — attributes hidden until hatching</span>
        )}
      </div>

      {/* Toast */}
      {buyResult && (
        <div className={`hatch-toast ${buyResult.success ? 'hatch-toast-ok' : 'hatch-toast-err'}`}>
          {buyResult.message}
          <button className="hatch-toast-close" onClick={() => setBuyResult(null)}>✕</button>
        </div>
      )}

      {/* On the Field — Hatched Players */}
      {view === 'field' && (
        playersLoading ? (
          <div className="hatch-loading">Loading players...</div>
        ) : filteredPlayers.length === 0 ? (
          <div className="hatch-empty">No available players match your filters.</div>
        ) : (
          <div className="hatch-grid">
            {filteredPlayers.map(p => (
              <div key={p.id} className="hatch-card" onClick={() => onNavigate?.(`/player/${p.id}`)}>
                <div className="hatch-card-top">
                  <img
                    src={p.avatar_url || '/robot-placeholder.png'}
                    alt={`${p.first_name} ${p.last_name}`}
                    className="hatch-avatar"
                    onError={e => { (e.target as HTMLImageElement).src = '/robot-placeholder.png'; }}
                  />
                  <div className="hatch-ovr-badge" style={{ background: getOvrColor(p.overall) }}>
                    <span className="hatch-ovr-num">{p.overall}</span>
                    <span className="hatch-ovr-grade">{getOvrGrade(p.overall)}</span>
                  </div>
                </div>

                <div className="hatch-card-body">
                  <div className="hatch-player-name">{p.first_name} {p.last_name}</div>
                  <div className="hatch-player-meta">
                    <span className="hatch-pos-tag">{p.position}</span>
                    <span className="hatch-team-tag">{p.team_name}</span>
                  </div>

                  <div className="hatch-stats-row">
                    {p.is_hitter ? (
                      <>
                        <span className="hatch-stat">PWR {p.power}</span>
                        <span className="hatch-stat">CON {p.contact}</span>
                        <span className="hatch-stat">SPD {p.speed}</span>
                        <span className="hatch-stat">DSC {p.discipline}</span>
                      </>
                    ) : (
                      <>
                        <span className="hatch-stat">VEL {p.velocity}</span>
                        <span className="hatch-stat">CTL {p.control}</span>
                        <span className="hatch-stat">MOV {p.movement}</span>
                        <span className="hatch-stat">END {p.endurance}</span>
                      </>
                    )}
                  </div>

                  <div className="hatch-card-footer">
                    <span className="hatch-price">{p.price.toLocaleString()} $BALLS</span>
                    <button
                      className="hatch-buy-btn"
                      disabled={buying === p.id || ballsBalance < p.price}
                      onClick={e => { e.stopPropagation(); setShowConfirm(p); }}
                    >
                      {buying === p.id ? '...' : ballsBalance < p.price ? 'Need more' : 'Buy Rights'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* In the Nest — Eggs */}
      {view === 'nest' && (
        playersLoading ? (
          <div className="hatch-loading">Loading eggs...</div>
        ) : filteredEggs.length === 0 ? (
          <div className="hatch-empty">No eggs available yet. They'll appear when NFTs are forged and paired.</div>
        ) : (
          <div className="hatch-grid">
            {filteredEggs.map(e => (
              <div key={e.id} className="hatch-card hatch-egg-card" onClick={() => onNavigate?.(`/player/${e.id}`)}>
                <div className="hatch-card-top hatch-egg-top">
                  <div className="hatch-egg-icon">🥚</div>
                  <div className="hatch-ovr-badge" style={{ background: getOvrColor(e.overall) }}>
                    <span className="hatch-ovr-num">{e.overall}</span>
                    <span className="hatch-ovr-grade">{getOvrGrade(e.overall)}</span>
                  </div>
                </div>

                <div className="hatch-card-body">
                  <div className="hatch-player-name">{e.first_name} {e.last_name}</div>
                  <div className="hatch-player-meta">
                    <span className="hatch-pos-tag">{e.position}</span>
                    <span className="hatch-egg-season">S{e.season_year}</span>
                  </div>

                  {/* Hidden attributes — mystery! */}
                  <div className="hatch-stats-row hatch-hidden-stats">
                    {e.is_hitter ? (
                      <>
                        <span className="hatch-stat hatch-hidden">PWR ???</span>
                        <span className="hatch-stat hatch-hidden">CON ???</span>
                        <span className="hatch-stat hatch-hidden">SPD ???</span>
                        <span className="hatch-stat hatch-hidden">DSC ???</span>
                      </>
                    ) : (
                      <>
                        <span className="hatch-stat hatch-hidden">VEL ???</span>
                        <span className="hatch-stat hatch-hidden">CTL ???</span>
                        <span className="hatch-stat hatch-hidden">MOV ???</span>
                        <span className="hatch-stat hatch-hidden">END ???</span>
                      </>
                    )}
                  </div>

                  <div className="hatch-card-footer">
                    <span className="hatch-price hatch-egg-price">{e.price.toLocaleString()} $BALLS</span>
                    <button
                      className="hatch-buy-btn hatch-egg-buy"
                      disabled={buying === e.id || ballsBalance < e.price}
                      onClick={ev => { ev.stopPropagation(); setShowConfirm(e); }}
                    >
                      {buying === e.id ? '...' : ballsBalance < e.price ? 'Need more' : 'Buy Egg'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Confirm modal */}
      {showConfirm && (
        <div className="hatch-modal-overlay" onClick={() => setShowConfirm(null)}>
          <div className="hatch-modal" onClick={e => e.stopPropagation()}>
            <h3 className="hatch-modal-title">Confirm Purchase</h3>
            <div className="hatch-modal-player">
              {'team_id' in showConfirm ? (
                <img src={(showConfirm as AIPlayer).avatar_url || '/robot-placeholder.png'} alt="" className="hatch-modal-avatar" />
              ) : (
                <div className="hatch-modal-egg-icon">🥚</div>
              )}
              <div>
                <div className="hatch-modal-name">{showConfirm.first_name} {showConfirm.last_name}</div>
                <div className="hatch-modal-meta">
                  {showConfirm.position} · OVR {showConfirm.overall}
                  {'team_name' in showConfirm && ` · ${(showConfirm as AIPlayer).team_name}`}
                </div>
              </div>
            </div>
            <div className="hatch-modal-price">
              <span>Price:</span>
              <span className="hatch-modal-amount">{showConfirm.price.toLocaleString()} $BALLS</span>
            </div>
            <p className="hatch-modal-note">
              {'team_id' in showConfirm
                ? 'Player stays on their current team. You own the rights — rename, portrait, and trade on the marketplace.'
                : 'Egg hatches at season flip. Attributes revealed then. Player enters the draft pool — worst record picks first.'}
            </p>
            <div className="hatch-modal-buttons">
              <button className="hatch-modal-cancel" onClick={() => setShowConfirm(null)}>Cancel</button>
              <button
                className="hatch-modal-confirm"
                disabled={buying !== null}
                onClick={() => handleBuy(showConfirm)}
              >
                {buying ? 'Buying...' : `Buy for ${showConfirm.price.toLocaleString()}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HatcheryPage;
